import React, { useState } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";

import { Expand } from "@sparefoot/react-commons";

import { Chevron } from "components/core/Icons";
import { H2 } from "components/core/Typography";
import { Heading } from "components/storages/Common";

import "./FacilityPageSection.scss";

/**
 * These are souped up versions of our expand component that are
 * styled for use on the facility page, where each section has a title.
 *
 * Some elements need to be wrapped in one of these for visual consistency,
 * but never collapse, like the nearby facilities section. These need to be
 * able to resize as necessary to flex between desktop and mobile where they
 * won't benefit from getting closed on mobile so their height can be
 * recalculated.
 *
 * Others are collapsable, like the sidebar sections. These can be defaulted
 * to being open or closed, but all appear open on desktop.
 *
 * Some collapsable ones, like the reviews, need to be able to grow
 * after they've been expanded, which is what `autoHeight` is for.
 *
 */
export default function FacilityPageSection({
	defaultOpen = false,
	isCollapsable = false,
	title,
	children,
	autoHeight = false,
	className,
	id
}) {
	const [accordionOpen, setAccordionOpen] = useState(defaultOpen);
	return (
		<div
			className={classnames(
				"facility-page-section",
				{ "default-open": defaultOpen },
				{ "not-collapsable": !isCollapsable },
				className
			)}
			id={id}
		>
			<div
				className="section-header"
				{...(isCollapsable
					? {
							onClick: () => {
								setAccordionOpen((prev) => !prev);
							}
						}
					: {})}
			>
				{title === "Units Available At Nearby Facilities" ? (
					<H2>{title}</H2>
				) : (
					<Heading
						content={title}
						level="2"
						size="standard"
					/>
				)}

				{isCollapsable && (
					<Chevron
						fill="gray"
						rotate={accordionOpen ? "up" : "down"}
					/>
				)}
			</div>
			<Expand
				expanded={accordionOpen}
				autoHeight={autoHeight}
			>
				<div className="content">{children}</div>
			</Expand>
		</div>
	);
}

FacilityPageSection.propTypes = {
	autoHeight: PropTypes.bool,
	defaultOpen: PropTypes.bool,
	isCollapsable: PropTypes.bool,
	title: PropTypes.string,
	children: PropTypes.node,
	className: PropTypes.string,
	id: PropTypes.string
};
